import * as React from 'react'
import { CompanyDetailsModel } from '@/app/models/companyDetailsModel'
import TableCheckbox from '@/modules/drivers/shared/tableItems/TableCheckbox'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { SelectionGroup } from '@/modules/drivers/context/TableContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { CompanyIcon } from '@/app/features/icons/Company'
import useFormatDate from '@/modules/drivers/hooks/useFormatDate'
import { FaIcon, faPenToSquare } from '@/lib/fa'

function formatSize(bytes: number | null | undefined): string {
  if (bytes == null) return '-'
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

interface CompanyRowProps {
  company: CompanyDetailsModel
  selectedIds: SelectionGroup
  toggleId: (id: string | number, isActive?: boolean) => void
  isSelectedAny: boolean
  search: string
  onClick?: () => void
  onEdit?: (company: CompanyDetailsModel) => void
  /** Total DB size for this company (bytes), provided by the parent bulk query. */
  totalSize?: number | null
  isSizeLoading?: boolean
}

const CompanyRow: React.FC<CompanyRowProps> = ({
  company,
  selectedIds,
  toggleId,
  isSelectedAny,
  search,
  onClick,
  onEdit,
  totalSize = null,
  isSizeLoading = false,
}) => {
  const { t } = useLocalize()
  const [isHovered, setIsHovered] = React.useState(false)

  // Must be the company PK — the action bar sends these ids straight to the
  // delete/activate/edit endpoints. `uniqueIdentifier` is a separate GUID
  // (the database identity) and never matches a company id server-side.
  const selectionId = company.id
  const isChecked = selectedIds?.ids.includes(selectionId)
  const isDisabled = company.deletedDate != null
  const isInactive = company.isActive === false && company.deletedDate == null

  const handleMouseEnter = () => setIsHovered(true)
  const handleMouseLeave = () => setIsHovered(false)

  const highlightMatch = (text: string, search: string) => {
    if (!search || !text) return text || '-'
    const regex = new RegExp(`(${search})`, 'gi')
    const parts = String(text).split(regex)

    return (
      <div>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <span key={index} className="tw-bg-[#FFF47B]">
              {part}
            </span>
          ) : (
            part
          ),
        )}
      </div>
    )
  }

  const { formatDate } = useFormatDate()
  const isValidText = company.isValid ? t('addCompany.upToDate') : t('addCompany.outdated')
  const formattedCreatedDate = company.createdDate ? formatDate(company.createdDate) : '-'
  const isDeleted = company.deletedDate != null
  const statusText = isDeleted ? t('addCompany.deleted') : (company.isActive === false ? t('common.inactiveM') : t('common.activeM'))

  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    // When a row click handler is provided, it replaces the default toggle-selection behavior.
    // Bulk-select then happens only via the checkbox.
    if (onClick) {
      onClick()
      return
    }
    toggleId(selectionId)
  }

  // Make row red when deletedDate exists (matching administration panel behavior)
  return (
    <tr
      className={`notches-border--right tw-w-fit tw-cursor-pointer tw-border-b tw-bg-white hover:tw-bg-primary/10 ${isChecked ? 'tw-bg-primary/10 tw-font-medium' : ''} ${isInactive ? 'tw-bg-gray-100 tw-text-gray-500' : ''}`}
      style={isDeleted ? { background: '#FFA8A0' } : {}}
      onClick={handleRowClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <TableCheckbox
        disabled={isDisabled}
        checked={isChecked}
        onChange={() => toggleId(selectionId)}
        isHovered={isHovered}
      />
      <TableTextRow
        icon={
          <CompanyIcon className="tw-h-5 tw-w-5 tw-text-content-base" />
        }
      >
        {highlightMatch(company.companyName || '', search)}
      </TableTextRow>
      <TableTextRow>
        {highlightMatch(company.uniqueIdentifier || '', search)}
      </TableTextRow>
      <TableTextRow>{isValidText}</TableTextRow>
      <TableTextRow>
        <span className={isDeleted ? 'tw-text-red-500' : (company.isActive === false ? 'tw-text-yellow-500' : 'tw-text-green-500')}>
          {statusText}
        </span>
      </TableTextRow>
      <TableTextRow>{formattedCreatedDate}</TableTextRow>
      <TableTextRow>
        {isSizeLoading ? <span className="tw-text-gray-400">…</span> : formatSize(totalSize)}
      </TableTextRow>
      <td className="tw-w-10 tw-px-2">
        {onEdit && !isDisabled && (
          <button
            type="button"
            title={t('common.edit')}
            onClick={(e) => {
              e.stopPropagation()
              onEdit(company)
            }}
            className={`tw-rounded tw-p-1.5 tw-text-content-secondary hover:tw-bg-primary/10 hover:tw-text-primary ${isHovered || isChecked ? 'tw-opacity-100' : 'tw-opacity-0'} tw-transition-opacity`}
          >
            <FaIcon icon={faPenToSquare} className="tw-h-4 tw-w-4" />
          </button>
        )}
      </td>
    </tr>
  )
}

export default CompanyRow

